import { filtersLibrary, periodsLibrary } from "@/components/filter/filter.types.ts";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { KpiTile } from "@/components/reporting/KpiTile.tsx";
import { LineChart } from "@/components/reporting/LineChart.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { DimensionBarsWidget } from "@/pages/reporting/components/DimensionBarsWidget.tsx";
import { metricLabelSuffix } from "@/pages/reporting/components/metric-label.ts";
import { ReportingWidget } from "@/pages/reporting/components/ReportingWidget.tsx";
import { useReportViewFilter } from "@/pages/reporting/components/use-report-view-filter.ts";
import {
  buildNullableWidgetDataBarsRows,
  widgetPointEntityIds,
  widgetSeriesToLineData,
} from "@/pages/reporting/components/widget-chart.adapter.ts";
import { PeopleAnalyticsCacheStatus } from "@/pages/reporting/employee/people-analytics/components/PeopleAnalyticsCacheStatus.tsx";
import { RetentionMetricSelector } from "@/pages/reporting/employee/retention/RetentionMetricSelector.tsx";
import { useRetentionWidget } from "@/pages/reporting/employee/retention/use-retention-widget.ts";
import {
  AnalyticReportTypes,
  ApiAnalyticsAggregationDate,
} from "@/types/analytics/analytics.types.ts";
import {
  ApiWidgetData,
  ApiWidgetDimension,
  ApiWidgetFilter,
  ApiWidgetFilterField,
  ApiWidgetMetric,
  ApiWidgetPeriodMode,
  ApiWidgetPreviewInput,
  ApiWidgetType,
} from "@/types/reporting/reporting.types.ts";
import { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ApiAggregationDate,
  Box,
  FontAwesomeIcon,
  Page,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface RetentionFilter {
  aggregation_date_mode?: ApiAnalyticsAggregationDate;
  date_from?: Date;
  date_to?: Date;
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
}

const previewLifecycleStatuses = Object.values(ApiEmployeeLifecycleStatus);

const PERCENT_METRICS = [
  ApiWidgetMetric.turnover_rate,
  ApiWidgetMetric.attrition_rate,
  ApiWidgetMetric.retention_rate,
];

export const EmployeeRetentionReport = () => {
  const { t } = useTranslation();
  const { canIView } = usePermissions();
  const { openModal } = useModals();
  const { formatPeriodDates } = useFormatter();
  const [metric, setMetric] = useState<ApiWidgetMetric>(ApiWidgetMetric.turnover_rate);

  const reportTitle = t("reporting:retention_report_title");

  useTitle(reportTitle, t("common:reports"));

  const library = filtersLibrary();
  const periodLibrary = periodsLibrary();

  const baseEmployeeFilters = [
    library.job_profile_ids,
    library.job_role_ids,
    library.department_ids,
    library.division_ids,
    library.location_ids,
    library.employment_type_ids,
    library.employment_contract_ids,
  ];

  const {
    filter: { filter },
    filterButton,
    config,
  } = useAppFilter<RetentionFilter>({
    filters: [
      library.aggregation_date_mode,
      { ...library.date_from, hideInFilter: true },
      { ...library.date_to, hideInFilter: true },
      { ...library.period, defaultValue: "rolling_year" },
      ...baseEmployeeFilters,
    ],
    source: AnalyticReportTypes.employeesHeadcount,
  });

  const widgetFilters = useMemo<ApiWidgetFilter[]>(() => {
    const result: ApiWidgetFilter[] = [];
    const push = (field: ApiWidgetFilterField, ids?: number[]) => {
      if (ids && ids.length) result.push({ field, ids });
    };
    push(ApiWidgetFilterField.job_profile_ids, filter.job_profile_ids);
    push(ApiWidgetFilterField.job_role_ids, filter.job_role_ids);
    push(ApiWidgetFilterField.department_ids, filter.department_ids);
    push(ApiWidgetFilterField.division_ids, filter.division_ids);
    push(ApiWidgetFilterField.location_ids, filter.location_ids);
    push(ApiWidgetFilterField.employment_type_ids, filter.employment_type_ids);
    push(ApiWidgetFilterField.employment_contract_ids, filter.employment_contract_ids);
    return result;
  }, [filter]);

  const dateGrain = filter.aggregation_date_mode ?? ApiAnalyticsAggregationDate.month;
  const viewFilterApplied = useReportViewFilter(baseEmployeeFilters);
  const ready = viewFilterApplied && !!filter.date_from && !!filter.date_to;

  const period = {
    mode: ApiWidgetPeriodMode.absolute,
    date_from: filter.date_from,
    date_to: filter.date_to,
  };

  const kpi = (m: ApiWidgetMetric): ApiWidgetPreviewInput => ({
    widget_type: ApiWidgetType.kpi,
    period,
    query: { metrics: [m], filters: widgetFilters },
  });

  const line = (m: ApiWidgetMetric): ApiWidgetPreviewInput => ({
    widget_type: ApiWidgetType.line_chart,
    period,
    query: { metrics: [m], filters: widgetFilters, date_grain: dateGrain },
  });

  const dimension = (m: ApiWidgetMetric, dim: ApiWidgetDimension): ApiWidgetPreviewInput => ({
    widget_type: ApiWidgetType.bar_chart,
    period,
    query: { metrics: [m], dimensions: [dim], filters: widgetFilters },
  });

  const turnoverKpi = useRetentionWidget(kpi(ApiWidgetMetric.turnover_rate), ready);
  const attritionKpi = useRetentionWidget(kpi(ApiWidgetMetric.attrition_rate), ready);
  const tenureKpi = useRetentionWidget(kpi(ApiWidgetMetric.average_tenure), ready);
  const ageKpi = useRetentionWidget(kpi(ApiWidgetMetric.average_age), ready);

  const isTurnoverKpiEmpty = turnoverKpi.data?.value === null || turnoverKpi.isError;
  const isAttritionKpiEmpty = attritionKpi.data?.value === null || attritionKpi.isError;
  const isTenureKpiEmpty = tenureKpi.data?.value === null || tenureKpi.isError;
  const isAgeKpiEmpty = ageKpi.data?.value === null || ageKpi.isError;

  const metricLine = useRetentionWidget(line(metric), ready);

  const byJobProfiles = useRetentionWidget(
    dimension(metric, ApiWidgetDimension.job_profile),
    ready
  );
  const byJobRoles = useRetentionWidget(dimension(metric, ApiWidgetDimension.job_role), ready);
  const byDepartments = useRetentionWidget(dimension(metric, ApiWidgetDimension.department), ready);
  const byDivisions = useRetentionWidget(dimension(metric, ApiWidgetDimension.division), ready);
  const byLocations = useRetentionWidget(dimension(metric, ApiWidgetDimension.location), ready);

  const isPercent = PERCENT_METRICS.includes(metric);
  // API serializes Decimal as a JSON string, so values arrive as strings — coerce before formatting.
  const formatValue = (value: number | string | null | undefined): string => {
    const safe = Number(value ?? 0);
    if (isPercent) return `${safe.toFixed(2)}%`;
    if (metric === ApiWidgetMetric.average_tenure)
      return `${safe.toFixed(1)} ${t("plurals:unit_months", { count: safe })}`;
    if (metric === ApiWidgetMetric.average_age)
      return `${safe.toFixed(1)} ${t("plurals:unit_years", { count: safe })}`;
    return `${safe.toFixed(1)}`;
  };

  const formatAxisValue = (value: number | string | null | undefined): string => {
    const safe = Number(value ?? 0);
    return isPercent ? `${safe.toFixed(2)}%` : `${safe.toFixed(1)}`;
  };

  const metricLineData = useMemo(
    () => widgetSeriesToLineData(metricLine.data, formatPeriodDates),
    [metricLine.data]
  );

  const source = AnalyticReportTypes.employeesHeadcount + " Retention";
  const metricDescriptionKey =
    metric === ApiWidgetMetric.average_tenure
      ? "reporting:metric_tenure_chart_description"
      : `reporting:metric_${metricLabelSuffix(metric)}_description`;

  const kpiTooltip = (description: ReactNode) => (
    <Tooltip>
      <TooltipTrigger>
        <FontAwesomeIcon icon={"fa-light fa-info-circle"} />
      </TooltipTrigger>
      <TooltipContent className={"max-w-[320px]"}>
        <Text size={"sm"}>{description}</Text>
      </TooltipContent>
    </Tooltip>
  );

  const handleLinePointClick = (seriesIndex: number, pointIndex: number) => {
    const ids = widgetPointEntityIds(metricLine.data, pointIndex, seriesIndex);
    if (!ids.length) return;
    openModal(PreviewEmployeesModal, {
      filter: {
        page_number: 1,
        employee_ids: ids,
        employee_lifecycle_statuses: previewLifecycleStatuses,
      },
      addColumns: [],
      modalTitle: metricLineData.labels[pointIndex],
      source: source + ", Over time",
    });
  };

  const dimensionWidget = (
    titleKey: string,
    byKey: string,
    data: ApiWidgetData | undefined,
    suffix: string
  ) => (
    <DimensionBarsWidget
      title={t(titleKey)}
      caption={`${t(`reporting:metric_${metricLabelSuffix(metric)}`)} ${t(byKey)}`}
      rows={buildNullableWidgetDataBarsRows(data, formatValue, (employeeIds, label) =>
        openModal(PreviewEmployeesModal, {
          filter: {
            page_number: 1,
            employee_ids: employeeIds,
            employee_lifecycle_statuses: previewLifecycleStatuses,
          },
          addColumns: [],
          modalTitle: label,
          source: source + ", " + suffix,
        })
      )}
    />
  );

  return (
    <Box className={"bg-light/80 h-full overflow-y-auto"}>
      <Page.Header title={reportTitle}>
        <Stack
          direction={"horizontal"}
          gap={"sm"}
          items={"center"}
          className={"overflow-hidden flex-wrap"}
        >
          {canIView(ApiPermission.reporting_people_analytics) && <PeopleAnalyticsCacheStatus />}
          <RetentionMetricSelector value={metric} onChange={setMetric} />

          <FilterChoosePeriod
            availableAggregationDateModes={[
              ApiAggregationDate.year,
              ApiAggregationDate.quarter,
              ApiAggregationDate.month,
              ApiAggregationDate.week,
            ]}
            staticPeriods={[
              periodLibrary.rolling_year,
              periodLibrary.year_to_date,
              periodLibrary.last_year_0,
              periodLibrary.last_year_2,
              periodLibrary.last_year_3,
            ]}
            filtersConfig={config}
          />
          {filterButton()}
        </Stack>
      </Page.Header>

      <Page.Content>
        <Stack gap={"md"}>
          <Box className={twMerge("grid md:grid-cols-4 gap-md py-lg")}>
            <KpiTile
              title={t("reporting:metric_turnover")}
              titleIcon={kpiTooltip(t("reporting:metric_turnover_description"))}
              value={isTurnoverKpiEmpty ? "-" : turnoverKpi.data?.value}
              format={isTurnoverKpiEmpty ? undefined : "percent"}
            />
            <KpiTile
              title={t("reporting:metric_attrition")}
              titleIcon={kpiTooltip(t("reporting:metric_attrition_description"))}
              value={isAttritionKpiEmpty ? "-" : attritionKpi.data?.value}
              format={isAttritionKpiEmpty ? undefined : "percent"}
            />
            <KpiTile
              title={t("reporting:metric_tenure")}
              titleIcon={kpiTooltip(t("reporting:metric_tenure_description"))}
              value={isTenureKpiEmpty ? "-" : Number(tenureKpi.data?.value ?? 0) / 12}
              format={
                isTenureKpiEmpty
                  ? undefined
                  : (value) => `${value.toFixed(1)} ${t("plurals:unit_years", { count: value })}`
              }
            />
            <KpiTile
              title={t("reporting:metric_age")}
              titleIcon={kpiTooltip(t("reporting:metric_age_description"))}
              value={isAgeKpiEmpty ? "-" : ageKpi.data?.value}
              format={
                isAgeKpiEmpty
                  ? undefined
                  : (value) => `${value.toFixed(1)} ${t("plurals:unit_years", { count: value })}`
              }
            />
          </Box>

          <ReportingWidget
            title={t(`reporting:metric_${metricLabelSuffix(metric)}`)}
            tooltip={<Text size={"sm"}>{t(metricDescriptionKey)}</Text>}
            content={
              metricLine.data && metricLineData.labels.length > 0 ? (
                <LineChart
                  labels={metricLineData.labels}
                  series={[{ data: metricLineData.data }]}
                  formatTooltipValue={formatValue}
                  formatAxisValue={formatAxisValue}
                  beginAtZero={
                    metric !== ApiWidgetMetric.retention_rate &&
                    metric !== ApiWidgetMetric.average_age
                  }
                  yMax={metric === ApiWidgetMetric.retention_rate ? 100 : undefined}
                  onPointClick={handleLinePointClick}
                />
              ) : (
                <Skeleton className={"w-full h-[236px]"} />
              )
            }
          />

          <Box className="flex gap-(--gap-md)">
            <Stack gap="md" className="flex-1">
              {dimensionWidget(
                "core:job_profiles",
                "reporting:by_job_profiles",
                byJobProfiles.data,
                "Job profiles"
              )}
              {dimensionWidget(
                "core:divisions",
                "reporting:by_divisions",
                byDivisions.data,
                "Divisions"
              )}
            </Stack>
            <Stack gap="md" className="flex-1">
              {dimensionWidget(
                "core:job_roles",
                "reporting:by_job_roles",
                byJobRoles.data,
                "Job roles"
              )}
              {dimensionWidget(
                "core:departments",
                "reporting:by_departments",
                byDepartments.data,
                "Departments"
              )}
              {dimensionWidget(
                "common:locations",
                "reporting:by_locations",
                byLocations.data,
                "Locations"
              )}
            </Stack>
          </Box>
          {/*<MetricTile*/}
          {/*  title={t("reporting:average_turnover")}*/}
          {/*  value={`${Number(turnoverKpi.data?.value ?? 0).toFixed(2)}%`}*/}
          {/*/>*/}
        </Stack>
      </Page.Content>
    </Box>
  );
};
